import { Box, Card, FormControl, TextField, Button, Typography, MenuItem, Select, InputLabel, SelectChangeEvent } from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { useState, useEffect } from "react";
import { fetchError, showMessage } from "../../../../redux/actions/Common";
import { useParams } from "react-router-dom";
import AppConfirmDialog from "../../../../components/AppConfirmDialog";
import {
    getMyBusinessGroups,
    setCurrentBusinessGroup,
    updateBeaconGroupSettings,
    uploadSettingsAsset,
    deleteSettingsAsset,
  } from "../../../../redux/actions";
  import { useAuthContext } from "../../../authentication/contexts/AuthContext";
import AssetUpload from "./AssetUpload";


type SettingsProps = {
    cancelSettingsForm: (event: any) => void;
    handleDeleteGroup: (event: any) => void;
};

const Settings: React.FC<SettingsProps> = ({ cancelSettingsForm, handleDeleteGroup}) => {

    const [themeColor, setThemeColor] = useState("#0C2340");
    const [title, setTitle] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [logo, setLogo] = useState<File | null>(null);
    const [subdomain, setSubdomain] = useState("");
    const [theme, setTheme] = useState("default");
    const [deleteGroupId, setDeleteGroupId] = useState<string>();
    const [iconError, setIconError] = useState<string>("");

    const [currentAssetFile, setCurrentAssetFile] = useState<File | undefined>(undefined);
    const [deleteAssetImage, setDeleteAssetImage] = useState(false);

    const [openDeleteGroupMessage, setOpenDeleteGroupMessage] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [initialValues, setInitialValues] = useState({
        themeColor: "",
        title: "",
        name: "",
        description: "",
        subdomain: "",
        theme: "",
        currentAssetFile: undefined,
        deleteAssetImage: false
    });

    const dispatch = useAppDispatch();
    let { group_id } = useParams();
    const businessGroupState = useAppSelector((state) => state.businessGroup);
    const auth = useAuthContext();

    useEffect(() => {
        if (businessGroupState.currentBusinessGroup) {

          const { theme, title, name, description, color, subdomain,  } = businessGroupState.currentBusinessGroup;
            setInitialValues({
                themeColor: color ?? "#0C2340",
                title: title ?? "",
                name: name ?? "",
                description: description ?? "",
                subdomain: subdomain ?? "",
                theme: theme ?? "default",
                currentAssetFile: undefined,
                deleteAssetImage: false
            });
            setTheme(theme ?? "default");
            setTitle(title ?? "");
            setDescription(description ?? "");
            setThemeColor(color ?? "#0C2340");
            setName(name ?? "");
            setSubdomain(subdomain ?? "");
        }
      }, [businessGroupState.currentBusinessGroup]);

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTitle(event.target.value);
      };
    
      const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);
      };
    
      const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDescription(event.target.value);
      };
    
      const handleThemeColorChange = (newValue: string) => {
        setThemeColor(newValue);
      };
    
      const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLogo(event.target.files?.[0] ?? null);
      };
    
      const handleSubdomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSubdomain(event.target.value);
      };

      const handleThemeChange = (event: SelectChangeEvent) => {
        if (businessGroupState.currentBusinessGroup) {
          setTheme(event.target.value as string);
        }
      };

      const handleUploadAsset = (groupid: string) => {
        if(currentAssetFile) {
            const formData = new FormData();
            setIsLoading(true);
    
            formData.append('file', currentAssetFile, currentAssetFile.name);
    
            dispatch(uploadSettingsAsset(groupid, formData,(result) => {
              if (result) {
                setCurrentAssetFile(undefined);
                setIconError("");
              }
              else{
                setIconError("Error uploading asset");
              }
              setIsLoading(false);
            }));
          }  
      };

      const handleDeleteAsset = (groupid: string) => {
        dispatch(deleteSettingsAsset(groupid, (result) => {
          if (result) {
            setDeleteAssetImage(false);
            setIconError("");
          }
          else{
            setIconError("Error uploading asset");
          }
        }));
      };

      const isFormChange = () => {
        return (
            themeColor !== initialValues.themeColor ||
            title !== initialValues.title ||
            name !== initialValues.name ||
            description !== initialValues.description ||
            subdomain !== initialValues.subdomain ||
            theme !== initialValues.theme ||
            currentAssetFile !== initialValues.currentAssetFile ||
            deleteAssetImage !== initialValues.deleteAssetImage
        );
    };

    const submitSettingsForm = (event: any) => {
        event.preventDefault();
        if (businessGroupState.currentBusinessGroup) {
          setIsLoading(true);
          dispatch(
            updateBeaconGroupSettings(
              businessGroupState.currentBusinessGroup.id,
              name,
              subdomain.toLowerCase(),
              theme,
              title,
              description,
              themeColor,
              (result: boolean) => {
                if (result) {
                  dispatch(showMessage("Settings have been saved."));
                  dispatch(
                    getMyBusinessGroups((businessGroups) => {
                      if (businessGroups && group_id) {
                        // Change the selected group when the URL includes business group id
                        const currentGroup = businessGroups.find((group) => group.id === group_id) ?? null;
                        dispatch(setCurrentBusinessGroup(currentGroup));
                      }
                      setIsLoading(false);
                    })
                  );
                } else {
                  dispatch(fetchError("Error updating settings."));
                  setIsLoading(false);
                }
              }
            )
          );
          if (currentAssetFile) {
            handleUploadAsset(businessGroupState.currentBusinessGroup.id);
          }
          if (deleteAssetImage) {
            handleDeleteAsset(businessGroupState.currentBusinessGroup.id);
          }
        }
    };

    return(
        <>
        <Card
        sx={{
          padding: 3,
          backgroundColor: "white",
        }}
      >
        <Typography sx={{marginX: 2}} variant="h6">Group Settings</Typography>
        {businessGroupState.currentBusinessGroup && (
          <Box sx={{ marginX: 2, marginY: 4 }} display="flex" flexDirection="column" gap={4}>
            <FormControl fullWidth>
              <TextField id="group" label="Experience Name" variant="outlined" size="small" value={name} onChange={handleNameChange} />
            </FormControl>
            <FormControl fullWidth>
                <TextField id="subdomain-input" label="Identifier" variant="outlined" size="small" value={subdomain} onChange={handleSubdomainChange} />
            </FormControl>
            <FormControl fullWidth>
              <TextField id="title" label="Landing Page Title" variant="outlined" size="small" value={title} onChange={handleTitleChange} />
            </FormControl>
            <FormControl fullWidth>
              <TextField
                id="description"
                label="Landing Page Description"
                aria-label="description"
                multiline
                rows={2}
                maxRows={4}
                value={description}
                onChange={handleDescriptionChange}
              />
            </FormControl>
            <Box display={"flex"} gap={2}>
              <FormControl fullWidth>
                <MuiColorInput
                  value={themeColor}
                  label="Landing Page Theme Color"
                  size="small"
                  format="hex8"
                  onChange={handleThemeColorChange}
                />
              </FormControl>
            </Box>
            <Box sx={{ maxWidth: 120 }}>
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label">Theme</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="theme-input"
                    label="Theme"
                    size="small"
                    value={theme}
                    onChange={handleThemeChange}
                  >
                    <MenuItem value={"default"}>Default</MenuItem>
                    <MenuItem value={"custom"}>Custom</MenuItem>
                    {auth.userRoles?.includes('admin') && (
                      <MenuItem value={"sxsw"}>SXSW</MenuItem>
                    )}
                    {auth.userRoles?.includes('admin') && (
                      <MenuItem value={"arr"}>ARR</MenuItem>
                    )}
                    {auth.userRoles?.includes('admin') && (
                      <MenuItem value={"paw"}>PAW</MenuItem>
                    )}
                    {auth.userRoles?.includes('admin') && (
                      <MenuItem value={"cruilla"}>Cruïlla</MenuItem>
                    )}
                    {auth.userRoles?.includes('admin') && (
                      <MenuItem value={"ufg"}>UFG</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <AssetUpload
                imageId={businessGroupState.currentBusinessGroup.has_custom_asset ? businessGroupState.currentBusinessGroup.id : undefined}
                handleFileChange={(file) => {
                  setCurrentAssetFile(file);
                }}
                handleDeleteAsset={(deleteAssetImage) => {
                  setDeleteAssetImage(deleteAssetImage);
                }}
                />
            </Box>
            <Box display="flex" gap={4} marginTop={2}>
              <Button variant="contained" sx={{ paddingX: 4, width: "max-content" }} onClick={submitSettingsForm} disabled={!isFormChange() || isLoading}>
                Save
              </Button>
              <Button variant="outlined" sx={{ paddingX: 4, width: "max-content" }} onClick={cancelSettingsForm} type="reset" disabled={!isFormChange() || isLoading}>
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </Card>
    <AppConfirmDialog
    open={openDeleteGroupMessage}
    onConfirm={() => {handleDeleteGroup(deleteGroupId); setOpenDeleteGroupMessage(false); setDeleteGroupId(undefined);}}
    onDeny={() => {
        setOpenDeleteGroupMessage(false);
    }}
    title="Are you sure you want to delete beacon group: "
    content={businessGroupState.currentBusinessGroup?.name + "?"}
    dialogTitle="Delete Beacon Group"
    />
    </>
    );

};
export default Settings;
